import { getToken } from "../storage/authStorage";
import { InvoicesResponseDTO, ZohoInvoiceDetailsResponseDTO } from "../models/InvoiceDTOs";
import { API_BASE_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export const getInvoicesForCustomer = async (
  customerZohoId: string,
  limit?: number,
  offset?: number,
  search?: string
): Promise<InvoicesResponseDTO> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append("customer_id", customerZohoId);
    if (limit !== undefined) {
      params.append("limit", limit.toString());
    }
    if (offset !== undefined) {
      params.append("offset", offset.toString());
    }
    if (search !== undefined && search.trim() !== "") {
      params.append("search", search.trim());
    }

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/getInvoiceForCustomer?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: "Failed to parse error response" };
      }

      throw new APIError(
        errorData.message || "Failed to fetch customer invoices",
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching customer invoices:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

export const getZohoInvoiceDetails = async (
  zohoInvoiceId: string
): Promise<ZohoInvoiceDetailsResponseDTO> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append("zoho_invoiceid", zohoInvoiceId);

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/accounting-invoice-details?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      let errorData;
      try {
        const text = await response.text();
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { message: text || "Failed to parse error response" };
        }
      } catch {
        errorData = { message: "Failed to parse error response" };
      }

      throw new APIError(
        errorData.message || "Failed to fetch Zoho invoice details",
        response.status,
        errorData
      );
    }

    // Parse response text to handle potential malformed JSON
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      // If JSON parsing fails, try to extract JSON from the text
      // This handles cases where there might be extra output before/after JSON
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          data = JSON.parse(jsonMatch[0]);
        } catch {
          throw new APIError(
            "Failed to parse response: Invalid JSON format",
            response.status
          );
        }
      } else {
        throw new APIError(
          "Failed to parse response: No JSON found",
          response.status
        );
      }
    }

    // Validate response structure
    if (!data || typeof data !== "object") {
      throw new APIError(
        "Invalid response format",
        response.status
      );
    }

    // Ensure response has the expected structure
    if (!data.success && !data.data) {
      throw new APIError(
        data.message || "Invalid response structure",
        response.status
      );
    }

    return data;
  } catch (error) {
    console.error("Error fetching Zoho invoice details:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};
